import { useEffect, useRef, useState, type ReactNode } from 'react';
import ImageExtension from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Youtube from '@tiptap/extension-youtube';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Heading2,
  Heading3,
  Image as ImageIcon,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo2,
  Video,
} from 'lucide-react';
import AppIcon from '../AppIcon';
import { blogService } from '../../services/blog/blogService';
import {
  BLOG_MEDIA_ACCEPT,
  isBlogHtmlContent,
  normalizeEditorHtml,
  plainTextToEditorHtml,
  resolveBlogMediaUrl,
  validateBlogMediaFile,
} from '../../utils/blog';

type BlogRichTextEditorProps = {
  id?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
};

type ToolbarButtonProps = {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
};

function ToolbarButton({ label, active, disabled, onClick, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      className={`blog-editor-toolbar-btn${active ? ' is-active' : ''}`}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function prepareEditorContent(value: string): string {
  const html = isBlogHtmlContent(value) ? value : plainTextToEditorHtml(value);
  if (!html) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('img[src]').forEach((img) => {
    const src = img.getAttribute('src');
    if (src) {
      img.setAttribute('src', resolveBlogMediaUrl(src));
    }
  });
  return doc.body.innerHTML;
}

export default function BlogRichTextEditor({
  id,
  value,
  onChange,
  placeholder = 'Blog içeriğinizi yazın…',
}: BlogRichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      ImageExtension.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'blog-editor-image',
          loading: 'lazy',
        },
      }),
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Youtube.configure({
        controls: true,
        nocookie: true,
        width: 640,
        height: 360,
        HTMLAttributes: {
          class: 'blog-editor-youtube',
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: prepareEditorContent(value),
    onUpdate: ({ editor: currentEditor }) => {
      onChange(normalizeEditorHtml(currentEditor.getHTML()));
    },
    editorProps: {
      attributes: {
        class: 'blog-editor-content',
        ...(id ? { id } : {}),
      },
    },
  });

  useEffect(() => {
    if (!editor) return;

    const nextContent = prepareEditorContent(value);
    const currentContent = normalizeEditorHtml(editor.getHTML());
    const normalizedNext = normalizeEditorHtml(nextContent);

    if (normalizedNext !== currentContent) {
      editor.commands.setContent(nextContent, { emitUpdate: false });
    }
  }, [editor, value]);

  if (!editor) {
    return <div className="blog-rich-editor blog-rich-editor-loading">Editör yükleniyor…</div>;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Bağlantı URL', previousUrl ?? 'https://');

    if (url === null) return;

    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  const insertImageFromUrl = () => {
    const url = window.prompt('Görsel URL', 'https://');
    if (!url?.trim()) return;

    editor.chain().focus().setImage({ src: url.trim() }).run();
  };

  const insertYoutubeVideo = () => {
    const url = window.prompt('YouTube video URL', 'https://www.youtube.com/watch?v=');
    if (!url?.trim()) return;

    editor.commands.setYoutubeVideo({ src: url.trim() });
  };

  const handleImageUploadClick = () => {
    setUploadError('');
    fileInputRef.current?.click();
  };

  const handleImageFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || !editor) return;

    const validationError = validateBlogMediaFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const uploadedPath = await blogService.uploadMedia(file);
      const imageUrl = resolveBlogMediaUrl(uploadedPath);
      editor.chain().focus().setImage({ src: imageUrl, alt: file.name.replace(/\.[^.]+$/, '') }).run();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Görsel yüklenemedi.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="blog-rich-editor">
      <input
        ref={fileInputRef}
        type="file"
        accept={BLOG_MEDIA_ACCEPT}
        hidden
        onChange={(event) => void handleImageFileChange(event)}
      />

      <div className="blog-editor-toolbar" role="toolbar" aria-label="Metin biçimlendirme">
        <ToolbarButton
          label="Kalın"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <AppIcon icon={Bold} size={18} />
        </ToolbarButton>
        <ToolbarButton
          label="İtalik"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <AppIcon icon={Italic} size={18} />
        </ToolbarButton>
        <ToolbarButton
          label="Altı çizili"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <AppIcon icon={UnderlineIcon} size={18} />
        </ToolbarButton>
        <ToolbarButton
          label="Üstü çizili"
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <AppIcon icon={Strikethrough} size={18} />
        </ToolbarButton>

        <span className="blog-editor-toolbar-divider" aria-hidden="true" />

        <ToolbarButton
          label="Başlık 2"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <AppIcon icon={Heading2} size={18} />
        </ToolbarButton>
        <ToolbarButton
          label="Başlık 3"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <AppIcon icon={Heading3} size={18} />
        </ToolbarButton>

        <span className="blog-editor-toolbar-divider" aria-hidden="true" />

        <ToolbarButton
          label="Madde işaretli liste"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <AppIcon icon={List} size={18} />
        </ToolbarButton>
        <ToolbarButton
          label="Numaralı liste"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <AppIcon icon={ListOrdered} size={18} />
        </ToolbarButton>
        <ToolbarButton
          label="Alıntı"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <AppIcon icon={Quote} size={18} />
        </ToolbarButton>
        <ToolbarButton label="Bağlantı ekle" active={editor.isActive('link')} onClick={setLink}>
          <AppIcon icon={Link2} size={18} />
        </ToolbarButton>
        <ToolbarButton label="Ayırıcı çizgi" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <AppIcon icon={Minus} size={18} />
        </ToolbarButton>

        <span className="blog-editor-toolbar-divider" aria-hidden="true" />

        <ToolbarButton
          label="Görsel yükle"
          disabled={uploading}
          onClick={handleImageUploadClick}
        >
          <AppIcon icon={ImagePlus} size={18} />
        </ToolbarButton>
        <ToolbarButton label="Görsel URL" onClick={insertImageFromUrl}>
          <AppIcon icon={ImageIcon} size={18} />
        </ToolbarButton>
        <ToolbarButton label="YouTube video" onClick={insertYoutubeVideo}>
          <AppIcon icon={Video} size={18} />
        </ToolbarButton>

        <span className="blog-editor-toolbar-divider" aria-hidden="true" />

        <ToolbarButton
          label="Geri al"
          disabled={!editor.can().chain().focus().undo().run()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <AppIcon icon={Undo2} size={18} />
        </ToolbarButton>
        <ToolbarButton
          label="Yinele"
          disabled={!editor.can().chain().focus().redo().run()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <AppIcon icon={Redo2} size={18} />
        </ToolbarButton>
      </div>

      {uploading && <p className="blog-editor-status">Görsel yükleniyor…</p>}
      {uploadError && <p className="blog-editor-error">{uploadError}</p>}

      <EditorContent editor={editor} />
    </div>
  );
}
